import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  ClipboardList,
  Download,
  Upload,
  Loader2,
  FileSpreadsheet,
  RefreshCw,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Tag,
  Package,
  Scissors,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminJointsPage = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_rows: 0, total_joints: 0, total_cuts: 0 });
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("");
  const [collapsedBatches, setCollapsedBatches] = useState(new Set());
  const [globalImportBusy, setGlobalImportBusy] = useState(false);
  const [importBusyBatch, setImportBusyBatch] = useState(null);  // sheet_id currently importing
  const globalFileRef = useRef(null);
  const batchFileRefs = useRef({});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProject) params.project_ref = filterProject;
      const { data } = await axios.get(`${API}/reports/joints`, { params });
      setRows(data.rows || []);
      setSummary(data.summary || { total_rows: 0, total_joints: 0, total_cuts: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [filterProject]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // ---- Batch grouping ----
  const batches = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (!map.has(r.sheet_id)) {
        map.set(r.sheet_id, {
          sheet_id: r.sheet_id,
          batch_name: r.batch_name,
          project_ref: r.project_ref,
          rows: [],
        });
      }
      map.get(r.sheet_id).rows.push(r);
    });
    return Array.from(map.values())
      .map(b => ({
        ...b,
        rows: [...b.rows].sort((a, c) => (a.drawing_number || "").localeCompare(c.drawing_number || "")),
      }))
      .sort((a, b) => (b.batch_name || "").localeCompare(a.batch_name || ""));
  }, [rows]);

  const toggleBatch = (sheet_id) => {
    setCollapsedBatches(prev => {
      const next = new Set(prev);
      if (next.has(sheet_id)) next.delete(sheet_id); else next.add(sheet_id);
      return next;
    });
  };

  const patchRowLocal = (sheet_id, row_id, patch) => {
    setRows(prev => prev.map(r =>
      r.sheet_id === sheet_id && r.row_id === row_id ? { ...r, ...patch } : r
    ));
  };

  const setCount = async (sheet_id, row_id, field, valueRaw) => {
    const trimmed = String(valueRaw ?? "").trim();
    const value = trimmed === "" ? null : Math.max(0, Math.min(200, parseInt(trimmed, 10)));
    if (trimmed !== "" && Number.isNaN(value)) return;
    patchRowLocal(sheet_id, row_id, { [field]: value });
    try {
      const endpoint = field === "joint_count" ? "joints" : "cuts";
      await axios.patch(`${API}/project-sheets/${sheet_id}/rows/${row_id}/${endpoint}`, {
        [field]: value,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
      fetchReport();
    }
  };

  const editProjectRef = async (batch) => {
    const val = window.prompt(`Set project reference for "${batch.batch_name}" (e.g. Amgen ADL2). Leave empty to clear.`, batch.project_ref || "");
    if (val === null) return;
    try {
      await axios.patch(`${API}/project-sheets/${batch.sheet_id}/project-ref`, { project_ref: val.trim() });
      setRows(prev => prev.map(r =>
        r.sheet_id === batch.sheet_id ? { ...r, project_ref: val.trim() } : r
      ));
      toast.success("Project reference saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const bulkSet = async (batch, kind) => {
    const nice = kind === "joints" ? "joint count" : "cut count";
    const val = window.prompt(`Set ${nice} for empty rows in "${batch.batch_name}":`, "4");
    if (val === null) return;
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0 || n > 200) {
      toast.error("Enter a number between 0 and 200");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/project-sheets/${batch.sheet_id}/${kind}/bulk-set`, {
        value: n, only_empty: true,
      });
      toast.success(`Set ${data.changed} row(s) to ${n}`);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk set failed");
    }
  };

  const downloadFile = async (kind, opts = {}) => {
    try {
      const params = {};
      if (filterProject) params.project_ref = filterProject;
      if (opts.sheet_id) params.sheet_id = opts.sheet_id;
      const resp = await axios.get(`${API}/reports/joints/export.${kind}`, {
        params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = opts.sheet_id ? `_${opts.batch_name || 'batch'}` : '';
      link.setAttribute('download', `joints_report${suffix}_${Date.now()}.${kind}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(`${kind.toUpperCase()} export failed`);
    }
  };

  const runImport = async (file, scopeLabel = "all batches") => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: dry } = await axios.post(`${API}/reports/joints/import?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const proceed = window.confirm(
        `Dry run for ${scopeLabel}:\n\n` +
        `${dry.would_update} row(s) will be updated, ` +
        `${dry.unchanged} unchanged, ` +
        `${dry.errors.length} error(s).\n\nProceed?`
      );
      if (!proceed) return;
      const fd2 = new FormData();
      fd2.append("file", file);
      const { data } = await axios.post(`${API}/reports/joints/import`, fd2, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.updated} rows`);
      await fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
  };

  const handleGlobalImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGlobalImportBusy(true);
    await runImport(file, "all batches");
    setGlobalImportBusy(false);
    if (globalFileRef.current) globalFileRef.current.value = "";
  };

  const handleBatchImport = async (batch, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusyBatch(batch.sheet_id);
    await runImport(file, batch.batch_name);
    setImportBusyBatch(null);
    if (batchFileRefs.current[batch.sheet_id]) {
      batchFileRefs.current[batch.sheet_id].value = "";
    }
  };

  const uniqueProjects = Array.from(new Set(rows.map(r => r.project_ref).filter(Boolean))).sort();

  return (
    <div className="min-h-screen bg-brand-50 flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-indigo-600 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-900">Joint & Cut Report</h1>
            <p className="text-sm text-brand-500">Track joints and cuts per drawing, grouped by batch. Feeds Amgen ADL2 and other project valuations.</p>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Tile label="Total Joints" value={summary.total_joints} tone="indigo" testid="tile-total-joints" icon={Link2} />
          <Tile label="Total Cuts" value={summary.total_cuts} tone="rose" testid="tile-total-cuts" icon={Scissors} />
          <Tile label="Total Rows" value={summary.total_rows} tone="blue" testid="tile-total-rows" icon={Package} />
        </div>

        {/* Filters + global actions */}
        <div className="bg-white border border-brand-200 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-brand-500 font-medium">Project Ref</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="text-sm border border-brand-200 rounded-md px-2 py-1.5"
              data-testid="filter-project"
            >
              <option value="">All</option>
              {uniqueProjects.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <Button variant="outline" onClick={fetchReport} data-testid="refresh-report">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <div className="flex-grow"></div>
          <span className="text-xs text-brand-400 mr-2">Global (all batches):</span>
          <Button variant="outline" onClick={() => downloadFile('csv')} data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => downloadFile('xlsx')} data-testid="export-xlsx">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <label className={`inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm font-medium ${globalImportBusy ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-indigo-300 text-indigo-700 hover:bg-indigo-50'}`}>
            {globalImportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import CSV
            <input
              ref={globalFileRef}
              type="file"
              accept=".csv"
              onChange={handleGlobalImport}
              disabled={globalImportBusy}
              className="hidden"
              data-testid="global-import-csv"
            />
          </label>
        </div>

        {/* Batch-grouped sections */}
        {loading ? (
          <div className="bg-white border border-brand-200 rounded-xl p-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-400" />
          </div>
        ) : batches.length === 0 ? (
          <div className="bg-white border border-brand-200 rounded-xl p-10 text-center text-brand-400 italic">
            No batches match the current filter.
          </div>
        ) : (
          <div className="space-y-4">
            {batches.map(batch => {
              const isCollapsed = collapsedBatches.has(batch.sheet_id);
              const totalJoints = batch.rows.reduce((s, r) => s + (Number(r.joint_count) || 0), 0);
              const totalCuts = batch.rows.reduce((s, r) => s + (Number(r.cut_count) || 0), 0);
              const importingHere = importBusyBatch === batch.sheet_id;
              return (
                <div key={batch.sheet_id} className="bg-white border border-brand-200 rounded-xl overflow-hidden" data-testid={`batch-section-${batch.sheet_id}`}>
                  {/* Batch header */}
                  <div className="bg-gradient-to-r from-indigo-50 to-brand-50 border-b border-brand-200 px-4 py-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleBatch(batch.sheet_id)}
                      className="text-brand-600 hover:text-brand-900 transition-colors"
                      data-testid={`toggle-batch-${batch.sheet_id}`}
                    >
                      {isCollapsed ? <ChevronRightIcon className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <Package className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-heading font-semibold text-brand-900">{batch.batch_name}</h3>
                    <div className="flex items-center gap-1 ml-2">
                      <Tag className="w-3.5 h-3.5 text-indigo-500" />
                      {batch.project_ref ? (
                        <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200" data-testid={`batch-project-ref-${batch.sheet_id}`}>
                          {batch.project_ref}
                        </Badge>
                      ) : (
                        <span className="text-xs text-brand-400 italic">no project ref</span>
                      )}
                      <button
                        type="button"
                        onClick={() => editProjectRef(batch)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 underline ml-1"
                        data-testid={`edit-project-ref-${batch.sheet_id}`}
                      >
                        edit
                      </button>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 ml-2" data-testid={`batch-joints-${batch.sheet_id}`}>
                      <Link2 className="w-3 h-3 mr-1" /> {totalJoints} joints
                    </Badge>
                    <Badge className="bg-rose-100 text-rose-800 border-rose-200" data-testid={`batch-cuts-${batch.sheet_id}`}>
                      <Scissors className="w-3 h-3 mr-1" /> {totalCuts} cuts
                    </Badge>
                    <span className="text-xs text-brand-400 ml-1">({batch.rows.length} rows)</span>

                    {/* Per-batch actions */}
                    <div className="ml-auto flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => bulkSet(batch, "joints")}
                          className="text-xs px-2 py-1 rounded-md bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid={`bulk-set-joints-${batch.sheet_id}`}
                          title="Set joint count on rows that don't have one yet"
                        >
                          Bulk joints…
                        </button>
                        <button
                          type="button"
                          onClick={() => bulkSet(batch, "cuts")}
                          className="text-xs px-2 py-1 rounded-md bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`bulk-set-cuts-${batch.sheet_id}`}
                          title="Set cut count on rows that don't have one yet"
                        >
                          Bulk cuts…
                        </button>
                      </div>
                      <div className="h-5 w-px bg-brand-200 mx-1" />
                      <button
                        type="button"
                        onClick={() => downloadFile('csv', { sheet_id: batch.sheet_id, batch_name: batch.batch_name })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white border border-brand-300 text-brand-700 hover:bg-brand-50"
                        data-testid={`batch-export-csv-${batch.sheet_id}`}
                        title="Export just this batch as CSV"
                      >
                        <Download className="w-3 h-3" /> CSV
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadFile('xlsx', { sheet_id: batch.sheet_id, batch_name: batch.batch_name })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white border border-brand-300 text-brand-700 hover:bg-brand-50"
                        data-testid={`batch-export-xlsx-${batch.sheet_id}`}
                        title="Export just this batch as Excel"
                      >
                        <FileSpreadsheet className="w-3 h-3" /> Excel
                      </button>
                      <label className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md cursor-pointer ${importingHere ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed' : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}>
                        {importingHere ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Import
                        <input
                          ref={(el) => { batchFileRefs.current[batch.sheet_id] = el; }}
                          type="file"
                          accept=".csv"
                          onChange={(e) => handleBatchImport(batch, e)}
                          disabled={importingHere}
                          className="hidden"
                          data-testid={`batch-import-csv-${batch.sheet_id}`}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Rows table */}
                  {!isCollapsed && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`batch-table-${batch.sheet_id}`}>
                        <thead className="bg-brand-100/50">
                          <tr className="text-left text-xs font-semibold text-brand-700">
                            <th className="px-3 py-2 w-32">Drawing #</th>
                            <th className="px-3 py-2 w-40">Eng Drawing #</th>
                            <th className="px-3 py-2 text-center w-28">Joints</th>
                            <th className="px-3 py-2 text-center w-28">Cuts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batch.rows.map((r) => (
                            <tr key={r.row_id} className="border-t border-brand-100 hover:bg-brand-50/40">
                              <td className="px-3 py-2 font-medium text-brand-900" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                                {r.drawing_number}
                              </td>
                              <td className="px-3 py-2 text-brand-600" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                                {r.eng_drawing_number || "—"}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="number"
                                  min={0}
                                  max={200}
                                  value={r.joint_count ?? ''}
                                  onChange={(e) => setCount(r.sheet_id, r.row_id, "joint_count", e.target.value)}
                                  placeholder="—"
                                  className={`w-20 px-2 py-1 text-sm text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                    r.joint_count != null ? 'border-indigo-300 bg-indigo-50/40' : 'border-brand-200 bg-white'
                                  }`}
                                  style={{ fontFamily: '"Courier New", Courier, monospace' }}
                                  data-testid={`joint-count-input-${r.row_id}`}
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="number"
                                  min={0}
                                  max={200}
                                  value={r.cut_count ?? ''}
                                  onChange={(e) => setCount(r.sheet_id, r.row_id, "cut_count", e.target.value)}
                                  placeholder="—"
                                  className={`w-20 px-2 py-1 text-sm text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 ${
                                    r.cut_count != null ? 'border-rose-300 bg-rose-50/40' : 'border-brand-200 bg-white'
                                  }`}
                                  style={{ fontFamily: '"Courier New", Courier, monospace' }}
                                  data-testid={`cut-count-input-${r.row_id}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

const Tile = ({ label, value, tone, testid, icon: Icon }) => {
  const tones = {
    indigo: { grad: 'from-indigo-500 to-indigo-600', chip: 'bg-indigo-100 text-indigo-700' },
    rose: { grad: 'from-rose-500 to-rose-600', chip: 'bg-rose-100 text-rose-700' },
    blue: { grad: 'from-blue-500 to-blue-600', chip: 'bg-blue-100 text-blue-700' },
  };
  const t = tones[tone] || tones.indigo;
  return (
    <div className="bg-white border border-brand-200 rounded-xl p-4 shadow-soft" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-1 w-12 bg-gradient-to-r ${t.grad} rounded-full mb-3`} />
        {Icon && <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.chip}`}><Icon className="w-4 h-4" /></div>}
      </div>
      <p className="text-xs text-brand-500 uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-3xl font-bold text-brand-900 mt-1">{value}</p>
    </div>
  );
};

export default AdminJointsPage;
